import { useCallback, useEffect, useMemo } from 'react';

import type { DocumentElement, FootersElement, HeadersElement } from '@aline/common';
import { createDefaultDocumentParts, createDocumentRoute, Queries } from '@aline/common';
import type { ApolloCache, ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { format, parseISO } from 'date-fns';
import { equals, uniqBy } from 'ramda';
import { flushSync } from 'react-dom';
import type { Editor } from 'slate';
import { useSetUserEditingPermissions } from 'src/editor/AlineEditor';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  documentTypename,
  doNothing,
  extractionQuestionTypename,
  useDocumentParams,
  useMaybeDocumentParams,
  userTypename,
} from 'src/helpers';
import { openLink } from 'src/helpers/url';
import { refetchDocumentCommentQueries } from 'src/hooks/comment';
import { Sentry } from 'src/sentry';
import type { UserType } from 'src/types';
import { getWorkflowWasSentForSig } from 'src/workflow';

import {
  deleteEditorUserStateForEditor,
  getEditorUserStateForTabId,
  setEditorUserStateForEditor,
} from './editor-user-state';
import { activeReportVar, refreshDataGrid, updatedCachedReportRows } from './reports';
import { useIsAuthorizedToEditStorable } from './storable';

type DocumentType = NonNullable<Apollo.GetDocumentBasicMetaQueryResult['data']>['document'];

/**
 * Returns the document identified by the nearest `:documentId` route param.
 * Pass an explicit `documentId` to override — callers rendered outside the
 * `/doc/:documentId/*` match (e.g., the global drag overlay in AppLayout)
 * should pass `useDocumentIdFromUrl()`.
 */
export function useGetCurrentDoc(documentIdOverride?: UUID | null): {
  document: DocumentType | null;
  loading: boolean;
} {
  const { documentId: documentIdFromRoute } = useMaybeDocumentParams();
  const documentId = documentIdOverride ?? documentIdFromRoute;
  const { data, loading } = Apollo.useGetDocumentBasicMetaQuery({
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });

  return {
    document: data?.document ?? null,
    loading,
  };
}

export function useGetSignedDocumentSimple(documentId: UUID) {
  const { data, loading } = Apollo.useGetSignedDocumentSimpleQuery({ variables: { documentId } });
  return { document: data?.document ?? null, loading };
}

export function useDocumentSignatureStatus(documentId: UUID): {
  document: NonNullable<Apollo.GetSignedDocumentQueryResult['data']>['document'] | null;
  isSigned: boolean;
  signedAt: string | null;
  wasSentForSignature: boolean;
} {
  const { data } = Apollo.useGetSignedDocumentQuery({ variables: { documentId } });
  const document = data?.document ?? null;

  return {
    document,
    isSigned: Boolean(document?.isSigned),
    signedAt: document?.envelope?.completedAt
      ? format(parseISO(document.envelope.completedAt), 'MMM d, yyyy')
      : null,
    wasSentForSignature: getWorkflowWasSentForSig(document?.workflow ?? null),
  };
}

export function useSubscribeToDocumentUpdates(documentId: UUID): void {
  Apollo.useOnDocumentUpdatedSubscription({
    ignoreResults: true,
    onData: ({ client, data: subscriptionData }) => {
      const documentUpdated = subscriptionData.data?.documentUpdated;
      if (!documentUpdated) return;

      const updatedDocument = documentUpdated.document;
      if (updatedDocument) {
        client.writeFragment<
          NonNullable<
            Apollo.OnDocumentUpdatedSubscriptionResult['data']
          >['documentUpdated']['document']
        >({
          data: { ...updatedDocument },
          fragment: gql`
            fragment UpdateDocument on Document {
              __typename
              id
            }
          `,
          id: client.cache.identify({ __typename: documentTypename, id: updatedDocument.id }),
        });
      }

      if (documentUpdated.commentsChanged) {
        void refetchDocumentCommentQueries(client, documentId);
      }
    },
    shouldResubscribe: true,
    variables: { documentId },
  });
}

export function useSubscribeToDocumentsUpdate(): void {
  Apollo.useOnDocumentsUpdatedSubscription({
    ignoreResults: true,
    onData: ({ client, data: subscriptionData }) => {
      const updatedDocuments = subscriptionData?.data?.documentsUpdated.documents ?? [];

      client.cache.batch({
        update: cache => {
          updatedDocuments.forEach(document => {
            document.questionJobs.forEach(job => {
              updateQuestionCache(cache, {
                documentId: document.id,
                job,
                questionId: job.question.id,
              });
            });
          });
        },
      });
    },
    onError: error => {
      Sentry.captureException(error, { extra: { subscription: 'DocumentsUpdated' } });
    },
    shouldResubscribe: true,
  });
}

export function createQuestionJobKey(documentId: UUID) {
  const jobKey: keyof Apollo.ExtractionQuestion = 'extractionJob';
  return `${jobKey}({"documentId":"${documentId}"})`;
}

export function updateQuestionCache<
  T extends Pick<Apollo.ExtractionJob, 'id' | '__typename'> = Pick<
    Apollo.ExtractionJob,
    'id' | '__typename'
  >,
>(
  cache: ApolloCache,
  options: {
    documentId: UUID;
    job: T;
    questionId: UUID;
  }
) {
  const { documentId, job, questionId } = options;

  cache.modify({
    fields: {
      [createQuestionJobKey(documentId)]: () => {
        const ref = cache.writeFragment<unknown>({
          data: job,
          fragment: gql`
            fragment NewJob on ExtractionJob {
              __typename
              id
            }
          `,
        });
        return ref ?? null;
      },
    },
    id: cache.identify({
      __typename: extractionQuestionTypename,
      id: questionId,
    }),
  });
}

export function useSubscribeToQuestionGenerationUpdates(): void {
  Apollo.useOnQuestionGenerationUpdatedSubscription({
    ignoreResults: true,
    shouldResubscribe: true,
  });
}

export function useSubscribeToPlaybookGenerationUpdates(): void {
  Apollo.useOnPlaybookGenerationJobUpdatedSubscription({
    ignoreResults: true,
    onData: ({ client, data: subscriptionData }) => {
      const generatedPlaybook =
        subscriptionData.data?.playbookGenerationJobUpdated.generationJob?.playbook;
      if (!generatedPlaybook) return;

      const newPlaybookRef = client.cache.writeFragment({
        data: generatedPlaybook,
        fragment: gql`
          fragment NewPlaybook on Playbook {
            __typename
            id
          }
        `,
      });
      if (!newPlaybookRef) return;

      client.cache.modify({
        fields: {
          playbooks: (refs, { readField }) => {
            return uniqBy(ref => readField<UUID>('id', ref), [...refs, newPlaybookRef]);
          },
        },
      });
    },
    shouldResubscribe: true,
  });
}

export function useGetEditorDocument(
  documentId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetEditorDocumentQuery>[0]>, 'variables'>
): {
  document: NonNullable<Apollo.GetEditorDocumentQueryResult['data']>['document'] | null;
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetEditorDocumentQuery({
    ...options,
    variables: { documentId },
  });
  return { document: data?.document ?? null, loading };
}

export function useGetDocumentSimple(documentId: UUID | null): {
  document: NonNullable<Apollo.GetDocumentSimpleQueryResult['data']>['document'] | null;
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetDocumentSimpleQuery({
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });

  return { document: data?.document ?? null, loading };
}

export function useGetDocumentForwardingSettings(
  documentId: UUID,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetDocumentForwardSettingsQuery>[0]>,
    'variables'
  >
): {
  document: NonNullable<Apollo.GetDocumentForwardSettingsQueryResult['data']>['document'] | null;
  loading: boolean;
  refetch: Apollo.GetDocumentForwardSettingsQueryResult['refetch'];
} {
  const { data, loading, refetch } = Apollo.useGetDocumentForwardSettingsQuery({
    ...options,
    variables: { documentId },
  });

  return { document: data?.document ?? null, loading, refetch };
}

export function useGetDocumentsSimple(
  documentIds: UUID[],
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetDocumentsSimpleQuery>[0]>, 'variables'>
): {
  documents: NonNullable<Apollo.GetDocumentsSimpleQueryResult['data']>['documents'];
  loading: boolean;
} {
  const { data, loading, previousData } = Apollo.useGetDocumentsSimpleQuery({
    ...options,
    variables: { documentIds },
  });

  return {
    // Using previousData to avoid a flash of loading state.
    documents: data?.documents ?? previousData?.documents ?? [],
    loading,
  };
}

export function useGetDocumentWithWorkflowState(documentId: UUID) {
  const { data, loading } = Apollo.useGetDocumentWithWorkflowStateQuery({
    variables: { documentId },
  });
  return { document: data?.document ?? null, loading };
}

interface DocumentWithOwner extends Pick<Apollo.Document, 'id' | '__typename'> {
  owner: {
    id: UUID;
    org: Pick<Apollo.Org, 'id' | '__typename'> | null;
  };
}

export function getIsUserInDocOwnerOrg(
  currentUser: UserType,
  document: DocumentWithOwner | null
): boolean {
  const isCurrentUserInDocOwnerOrg = Boolean(
    document?.owner?.org && currentUser?.org?.id === document.owner.org.id
  );

  return currentUser?.id === document?.owner?.id || isCurrentUserInDocOwnerOrg;
}

export function useGetDocumentVersions(
  documentId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetDocumentVersionsQuery>[0]>, 'variables'>
): {
  document: NonNullable<Apollo.GetDocumentVersionsQueryHookResult['data']>['document'] | null;
  loading: boolean;
  refetch: Apollo.GetDocumentVersionsQueryHookResult['refetch'];
} {
  const { data, loading, refetch } = Apollo.useGetDocumentVersionsQuery({
    ...options,
    variables: { documentId },
  });

  return {
    document: data?.document ?? null,
    loading,
    refetch,
  };
}
export function useGetDocument(
  documentId: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetDocumentQuery>[0]>, 'variables'>
): {
  document: NonNullable<Apollo.GetDocumentQueryHookResult['data']>['document'] | null;
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetDocumentQuery({
    ...options,
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });
  return {
    document: data?.document ?? null,
    loading,
  };
}
export function useGetDocumentWithLatestVersion(
  documentId: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetDocumentLatestVersionQuery>[0]>,
    'variables'
  >
): {
  document: NonNullable<Apollo.GetDocumentLatestVersionQueryHookResult['data']>['document'] | null;
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetDocumentLatestVersionQuery({
    ...options,
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });

  return {
    document: data?.document ?? null,
    loading,
  };
}

type CreateDocumentOptions = Parameters<Apollo.CreateDocumentMutationHookResult[0]>[0] & {
  reportId?: UUID;
};

export function useCreateDocument(): {
  createDocument: (
    options: CreateDocumentOptions
  ) => Promise<ApolloLink.Result<Apollo.CreateDocumentMutation>>;
  loading: boolean;
} {
  const currentUserId = useCurrentUserId();
  const [createDocument, { loading }] = Apollo.useCreateDocumentMutation();
  const handleCreateDocument = useCallback(
    async ({ reportId, ...apolloOpts }: CreateDocumentOptions) => {
      return createDocument({
        ...apolloOpts,
        update(cache, { data }) {
          const newDocumentRef = cache.writeFragment({
            data: data?.createDocument.document,
            fragment: gql`
              fragment CreateDocument on Document {
                __typename
                id
              }
            `,
          });
          if (!newDocumentRef) return;

          // Add to recent docs.
          const myRecentDocumentsKey: keyof Apollo.User = 'myRecentDocuments';
          if (currentUserId) {
            cache.modify({
              fields: {
                [myRecentDocumentsKey]: (refs = [], { readField }) => {
                  return uniqBy(ref => readField<UUID>('id', ref), [newDocumentRef, ...refs]);
                },
              },
              id: cache.identify({ __typename: userTypename, id: currentUserId }),
            });
          }

          if (reportId) {
            updatedCachedReportRows({
              action: { docRefs: [newDocumentRef], type: 'add' },
              cache,
              reportId,
            });

            const activeReport = activeReportVar();
            if (activeReport?.reportId === reportId) refreshDataGrid(activeReport.apiRef);
          }
        },
      });
    },
    [createDocument, currentUserId]
  );

  return {
    createDocument: handleCreateDocument,
    loading,
  };
}

export function useSetEditorUserState(
  editor: Editor,
  options: { isReadOnly?: boolean } = {}
): void {
  const { isReadOnly = false } = options;
  const currentUserId = useCurrentUserId();
  const runtime = useMaybeEditorRuntime();
  const { documentId } = useDocumentParams();
  const tabId = runtime?.editor === editor ? runtime.tabId : null;
  const existingState = getEditorUserStateForTabId(tabId) ?? editor.userState ?? null;
  const { document } = useGetEditorDocument(documentId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { canEdit: canEditDocument } = useIsAuthorizedToEditStorable(documentId);
  const newState = useMemo((): NonNullable<Editor['userState']> => {
    if (!document)
      return {
        editingUserId: currentUserId,
        isInSigningCeremony: false,
        // Make read-only until everything loads...
        isReadOnly: true,
        isSigned: false,
      };

    const state = Queries.createEditorUserState(editor, {
      currentUserId,
      document,
      envelope: document.envelope,
      workflow: document.workflow,
    });

    return { ...state, canEditDocument, isReadOnly: state.isReadOnly || isReadOnly };
  }, [canEditDocument, currentUserId, document, editor, isReadOnly]);
  const newDocumentInfo = useMemo((): Editor['documentInfo'] => {
    if (!document?.latestVersion || document.latestVersion.__typename !== 'DocumentVersion')
      return undefined;

    return {
      id: document.id,
      latestVersion: {
        __typename: 'DocumentVersion',
        id: document.latestVersion.id,
      },
      name: document.name || 'Untitled Document',
      variant: document.variant,
    };
  }, [document]);

  const statesEqual = equals(newState, existingState);
  const editorStatesEqual = equals(editor.userState, newState);
  const documentInfoEqual = equals(editor.documentInfo, newDocumentInfo);

  useEffect(() => {
    if (!statesEqual || !editorStatesEqual) {
      // Ensure that the var and editor.userState don't point to the same object since we mutate editor.userState directly.
      setEditorUserStateForEditor(editor, newState, tabId);
    }
  }, [editor, editorStatesEqual, newState, statesEqual, tabId]);

  /**
   * Keep cleanup lifecycle-scoped to the editor/tab pair. If this cleanup is tied
   * to user-state value changes, React runs it before republishing the next state,
   * clearing documentUi.userState and causing an infinite clear/republish loop.
   */
  useEffect(() => {
    return () => {
      deleteEditorUserStateForEditor(editor, tabId);
    };
  }, [editor, tabId]);

  useEffect(() => {
    if (!documentInfoEqual) editor.documentInfo = newDocumentInfo;
  }, [documentInfoEqual, editor, newDocumentInfo]);
}

/**
 * Delete documents from the repository trash.
 */
export function useDeleteTrashedDocuments() {
  const currentUserId = useCurrentUserId();
  const [deleteDocumentsMutation, { loading }] = Apollo.useDeleteDocumentsMutation();

  const deleteDocuments = useCallback(
    (options: Parameters<typeof deleteDocumentsMutation>[0]) => {
      return deleteDocumentsMutation({
        ...options,
        update(cache, { data }) {
          const result = data?.deleteDocuments;
          if (!result) return;

          for (const id of result.documentIds) {
            cache.evict({ id: cache.identify({ __typename: documentTypename, id }) });
          }

          if (currentUserId && result.documentIds.length > 0) {
            const deletedIds = new Set(result.documentIds);
            cache.modify({
              fields: {
                myRecentDocuments: (refs: readonly Reference[] = [], { readField }) =>
                  refs.filter(ref => {
                    const id = readField<UUID>('id', ref);
                    return id && !deletedIds.has(id);
                  }),
              },
              id: cache.identify({ __typename: userTypename, id: currentUserId }),
            });
          }

          cache.gc();
        },
      });
    },
    [currentUserId, deleteDocumentsMutation]
  );

  return { deleteDocuments, loading };
}

/**
 * Restore documents in the trash.
 */
export function useRestoreTrashedDocuments() {
  const [restoreDocumentsMutation] = Apollo.useRestoreDocumentsMutation();

  const restoreDocuments = useCallback(
    (options: Parameters<typeof restoreDocumentsMutation>[0]) => {
      return restoreDocumentsMutation({
        ...options,
        update(cache, { data }) {
          const result = data?.restoreDocuments;
          if (!result) return;

          for (const id of result.documentIds) {
            cache.evict({ id: cache.identify({ __typename: documentTypename, id }) });
          }

          cache.gc();
        },
      });
    },
    [restoreDocumentsMutation]
  );

  return { restoreDocuments };
}

export type UpdateCacheOnTrashArgs = {
  cache: ApolloCache;
  trashedDocumentIds: Set<UUID>;
};

export interface TrashDocuments {
  updateCacheOnTrash?: (args: UpdateCacheOnTrashArgs) => void;
}

export function useTrashDocuments({ updateCacheOnTrash = doNothing }: TrashDocuments = {}): {
  trashDocuments: (
    input: Apollo.DisableDocumentsInput,
    options?: Parameters<Apollo.DisableDocumentsMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.DisableDocumentsMutation>>;
} {
  const currentUserId = useCurrentUserId();
  const [trashDocuments] = Apollo.useDisableDocumentsMutation();

  return {
    trashDocuments: (input, options) => {
      return trashDocuments({
        ...options,
        update(cache, { data }, { variables }) {
          const result = data?.disableDocuments;
          const documentIds = result?.documentIds ?? [];
          const skippedDocumentIds = result?.skippedDocumentIds ?? [];
          const numSkipped = result?.numSkipped ?? 0;
          const runAllDocs = variables?.input?.runAllDocs ?? false;
          const trashedDocumentIds = new Set(documentIds);

          // Remove from myRecentDocuments.
          if (currentUserId) {
            // runAllDocs omits documentIds in the mutation response, so we cannot diff by trashed IDs.
            // numSkipped === 0 means every candidate document was trashed — nothing remains in scope, so clear recents.
            if (runAllDocs && numSkipped === 0) {
              cache.modify({
                fields: {
                  myRecentDocuments: () => [],
                },
                id: cache.identify({ __typename: userTypename, id: currentUserId }),
              });
            } else if (runAllDocs && skippedDocumentIds.length > 0) {
              const keep = new Set(skippedDocumentIds);
              cache.modify({
                fields: {
                  myRecentDocuments: (refs: readonly Reference[] = [], { readField }) => {
                    return refs.filter((ref: Reference) => {
                      const id = readField<UUID>('id', ref);
                      return id && keep.has(id);
                    });
                  },
                },
                id: cache.identify({ __typename: userTypename, id: currentUserId }),
              });
            } else {
              cache.modify({
                fields: {
                  myRecentDocuments: (refs: readonly Reference[] = [], { readField }) => {
                    return refs.filter((ref: Reference) => {
                      const id = readField<UUID>('id', ref);
                      return id && !trashedDocumentIds.has(id);
                    });
                  },
                },
                id: cache.identify({ __typename: userTypename, id: currentUserId }),
              });
            }
          }

          updateCacheOnTrash({
            cache,
            trashedDocumentIds,
          });
        },
        variables: { input },
      });
    },
  };
}

export function useDuplicateTemplate() {
  const currentUserId = useCurrentUserId();
  const [duplicateTemplate, { loading }] = Apollo.useDuplicateTemplateMutation();
  return {
    duplicateTemplate: (options: Parameters<Apollo.DuplicateTemplateMutationHookResult[0]>[0]) => {
      return duplicateTemplate({
        ...options,
        update(cache, { data }) {
          const newDocumentRef = cache.writeFragment({
            data: data?.duplicateTemplate.document,
            fragment: gql`
              fragment DuplicateTemplate on Document {
                __typename
                id
              }
            `,
          });

          // Add to recent docs.
          if (currentUserId && newDocumentRef) {
            cache.modify({
              fields: {
                myRecentDocuments: (refs: readonly Reference[] = [], { readField }) => {
                  return uniqBy(ref => readField<UUID>('id', ref), [newDocumentRef, ...refs]);
                },
              },
              id: cache.identify({ __typename: userTypename, id: currentUserId }),
            });
          }
        },
      });
    },
    loading,
  };
}

type CopyDocumentOptions = Parameters<Apollo.CopyDocumentMutationHookResult[0]>[0] & {
  reportId?: UUID;
};

export function useCopyDocument() {
  const currentUserId = useCurrentUserId();
  const [copyDocument, { loading }] = Apollo.useCopyDocumentMutation();
  return {
    copyDocument: ({ reportId, ...apolloOpts }: CopyDocumentOptions) => {
      return copyDocument({
        ...apolloOpts,
        update(cache, { data }) {
          const newDocumentRef = cache.writeFragment({
            data: data?.copyDocument.document,
            fragment: gql`
              fragment CopyDocument on Document {
                __typename
                id
              }
            `,
          });
          if (!newDocumentRef) return;

          // Add to recent docs.
          const myRecentDocumentsKey: keyof Apollo.User = 'myRecentDocuments';
          if (currentUserId) {
            cache.modify({
              fields: {
                [myRecentDocumentsKey]: (refs: readonly Reference[] = [], { readField }) => {
                  return uniqBy(ref => readField<UUID>('id', ref), [newDocumentRef, ...refs]);
                },
              },
              id: cache.identify({ __typename: userTypename, id: currentUserId }),
            });
          }

          if (reportId) {
            updatedCachedReportRows({
              action: { docRefs: [newDocumentRef], type: 'add' },
              cache,
              reportId,
            });

            const activeReport = activeReportVar();
            if (activeReport?.reportId === reportId) refreshDataGrid(activeReport.apiRef);
          }
        },
      });
    },
    loading,
  };
}

/**
 * Ensures user's editing privileges (i.e., PermissionRole and EditingMode) are kept up to date.
 *
 * `documentId` is listed so the sync also runs when the tab opens another document whose
 * permissions happen to match: `useBindEditorTabStateToDocument` has just dropped the previous
 * document's editing mode pick, and this resolves the new document's mode from the role.
 */
export function useSyncUserEditingPrivileges() {
  const { documentId } = useDocumentParams();
  const { setUserEditingPermissions } = useSetUserEditingPermissions();

  useEffect(() => {
    setUserEditingPermissions();
  }, [documentId, setUserEditingPermissions]);
}

export function useCreateAndOpenNewDocument() {
  const { createDocument, loading: creatingDocument } = useCreateDocument();
  const createAndOpenNewDocument = useCallback(
    async (args: {
      name: string;
      variant: Apollo.DocumentVariant;
      afterOpen?: () => void;
      aiChatItem?: string;
      documentElement?: DocumentElement;
      footersElement?: FootersElement;
      headersElement?: HeadersElement;
      reportId?: UUID;
    }) => {
      const { afterOpen = doNothing, name, reportId, variant } = args;
      const { data } = await createDocument({
        reportId,
        variables: {
          documentProps: {
            documentJSON: createDefaultDocumentParts({
              documentElement: args.documentElement,
              footersElement: args.footersElement,
              headersElement: args.headersElement,
            }),
            name,
            variant,
          },
        },
      });
      if (data?.createDocument.document) {
        const documentRoute = createDocumentRoute(data.createDocument.document.id, {
          aiChatItem: args.aiChatItem,
        });
        /**
         * Commit the dialog close before opening a tab so MUI fires onExit;
         * the tab switch gate can then wait for onExited before freezing this tab.
         */
        flushSync(afterOpen);
        openLink(documentRoute, { title: name });
        return;
      }

      afterOpen();
    },
    [createDocument]
  );

  return { createAndOpenNewDocument, creatingDocument };
}

export function useGetDocumentHeader(
  documentId: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetDocumentHeaderQuery>[0]>, 'variables'>
) {
  const { data, loading } = Apollo.useGetDocumentHeaderQuery({
    ...options,
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });

  return { document: data?.document ?? null, loading };
}

/**
 * Hook specifically designed for the DocumentSummaryDrawer's FileDetails component.
 * Returns the document data ready to be used by ContractTypePicker and RepositoryPartiesList.
 */
export function useGetDocumentForSummaryDisplay(
  documentId: UUID,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetDocumentFileDetailsQuery>[0]>,
    'variables'
  >
) {
  const { data, loading, refetch } = Apollo.useGetDocumentFileDetailsQuery({
    ...options,
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });
  return { document: data?.document ?? null, loading, refetch };
}
